import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Zap, Eye, Flame, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "../../lib/api";

export const SpotlightBanner = ({ onSelect = () => {} }) => {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    Promise.all([
      api.featured().catch(() => []),
      api.bundles().then((r) => r.bundles || []).catch(() => []),
    ]).then(([featured, bundles]) => {
      const list = [...featured];
      if (bundles[0]) list.unshift({ ...bundles[0], _isBundle: true });
      setItems(list);
    });
  }, []);

  useEffect(() => {
    if (items.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), 5000);
    return () => clearInterval(t);
  }, [items.length]);

  if (items.length === 0) return null;
  const p = items[idx];
  const isBundle = p._isBundle;
  const disc = isBundle ? p.discount_pct : (p.pricing?.discount_pct || 0);
  const price = isBundle ? p.bundle_price : p.base_price;
  const subtitle = isBundle ? `${p.count} templates · Bundle` : `${p.software_type} · ${p.tier}`;

  const act = () => {
    if (isBundle) document.getElementById("bundles")?.scrollIntoView({ behavior: "smooth" });
    else onSelect(p);
  };

  return (
    <section data-testid="spotlight-banner" className="border-y border-white/10 bg-slate-950">
      <div className="mx-auto max-w-[1400px] px-6 py-8 lg:px-10">
        <div className="mb-4 flex items-center justify-between">
          <span className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-emerald-400">
            <Flame className="h-4 w-4" /> Hot right now
          </span>
          <div className="flex items-center gap-2">
            <button
              data-testid="spotlight-prev"
              aria-label="Previous"
              onClick={() => setIdx((i) => (i - 1 + items.length) % items.length)}
              className="flex h-8 w-8 items-center justify-center border border-white/15 text-slate-300 transition-colors duration-200 hover:border-emerald-400 hover:text-white"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              data-testid="spotlight-next"
              aria-label="Next"
              onClick={() => setIdx((i) => (i + 1) % items.length)}
              className="flex h-8 w-8 items-center justify-center border border-white/15 text-slate-300 transition-colors duration-200 hover:border-emerald-400 hover:text-white"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={p.id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35 }}
            data-testid="spotlight-item"
            className="grid grid-cols-1 items-stretch gap-0 border border-white/10 md:grid-cols-2"
          >
            <div className="relative h-56 overflow-hidden md:h-72">
              <img src={p.image_url} alt={p.title} className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-slate-950/70" />
              <span className="absolute left-4 top-4 flex items-center gap-1 border border-emerald-500/40 bg-slate-950/80 px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-emerald-400 backdrop-blur">
                {isBundle ? "Bundle deal" : "New drop"}
              </span>
            </div>

            <div className="flex flex-col justify-center gap-3 p-7 lg:p-10">
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500">
                {subtitle}
              </span>
              <h3 className="font-display text-2xl font-extrabold tracking-tight text-white lg:text-3xl">
                {isBundle ? p.name : p.title}
              </h3>
              <p className="line-clamp-2 text-sm leading-relaxed text-slate-400">{p.description}</p>
              <div className="mt-1 flex flex-wrap items-center gap-4">
                <span className="font-mono text-3xl font-bold text-emerald-400">
                  {price === 0 ? "Free" : `$${price}`}
                </span>
                {isBundle && (
                  <span className="font-mono text-lg text-slate-500 line-through">${p.original_price}</span>
                )}
                {disc > 0 && (
                  <span className="flex items-center gap-1 font-mono text-xs uppercase tracking-wider text-emerald-500">
                    <Zap className="h-3 w-3" /> {isBundle ? `${disc}% off bundle` : `${disc}% below market`}
                  </span>
                )}
              </div>
              <button
                data-testid="spotlight-view-btn"
                onClick={act}
                className="mt-2 inline-flex w-fit items-center gap-2 bg-emerald-500 px-6 py-3 font-semibold text-slate-900 transition-[background-color] duration-200 hover:bg-emerald-400"
              >
                <Eye className="h-4 w-4" /> {isBundle ? "See the bundle" : "View template"}
              </button>
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="mt-4 flex items-center justify-center gap-1.5">
          {items.map((it, i) => (
            <button
              key={it.id}
              aria-label={`Go to ${it.title}`}
              onClick={() => setIdx(i)}
              className={`h-1.5 transition-all duration-300 ${i === idx ? "w-7 bg-emerald-400" : "w-1.5 bg-white/20 hover:bg-white/40"}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default SpotlightBanner;
